'use client';

import React, { useMemo } from 'react';
import { Box } from '@mui/material';

import { PriceChart } from '@/components';
import { useSportsPricesHistoryMultiple } from '@/services/query/sports.queries';
import { EventResponse } from '@/types';
import { getMarketTokenIds, getMarketType, parseMarketOutcomes } from '@/utils';

interface GamePriceChartProps {
  game: EventResponse;
}

export const GamePriceChart = ({ game }: GamePriceChartProps) => {
  const { moneylineMarkets, isMultiMarket } = useMemo(() => {
    const markets = game.markets?.filter((m) => getMarketType(m) === 'moneyline') || [];
    return {
      moneylineMarkets: markets,
      isMultiMarket: markets.length > 1,
    };
  }, [game]);

  const tokenIds = useMemo(() => {
    if (isMultiMarket) {
      return moneylineMarkets.map((m) => getMarketTokenIds(m)[0]).filter(Boolean);
    } else if (moneylineMarkets.length === 1) {
      return getMarketTokenIds(moneylineMarkets[0]);
    }
    return [];
  }, [moneylineMarkets, isMultiMarket]);

  const queries = useSportsPricesHistoryMultiple(tokenIds);

  const isLoading = queries.some((q) => q.isLoading);

  const historicalData = useMemo(() => {
    if (queries.length === 0 || tokenIds.length === 0) return [];

    const uniqueTimestamps = new Set<number>();
    const marketDataMaps: Map<number, number>[] = [];

    queries.forEach((query) => {
      const map = new Map<number, number>();
      if (Array.isArray(query.data)) {
        query.data.forEach((point: any) => {
          const ts = point.time || point.timestamp || 0;
          if (ts && typeof ts === 'number' && !isNaN(ts)) {
            uniqueTimestamps.add(ts);
            const price = typeof point.price === 'string' ? parseFloat(point.price) : point.price;
            if (price !== undefined && price !== null && !isNaN(price)) {
              map.set(ts, price);
            }
          }
        });
      }
      marketDataMaps.push(map);
    });

    const sortedTimestamps = Array.from(uniqueTimestamps).sort((a, b) => a - b);
    const lastKnownPrices: number[] = new Array(tokenIds.length).fill(-1);

    return sortedTimestamps.map((timestamp) => {
      const point: any = {
        timestamp,
        date: new Date(timestamp * 1000).toISOString(),
        values: {},
      };

      tokenIds.forEach((_, index) => {
        const teamKey = `team${index + 1}`;
        const map = marketDataMaps[index];
        const price = map?.get(timestamp);

        if (price !== undefined) {
          point.values[teamKey] = price;
          lastKnownPrices[index] = price;
        } else if (lastKnownPrices[index] !== -1) {
          point.values[teamKey] = lastKnownPrices[index];
        } else {
          point.values[teamKey] = null;
        }
      });
      return point;
    });
  }, [queries, tokenIds]);

  const chartMarkets = useMemo(() => {
    if (!game || tokenIds.length === 0) return [];

    if (isMultiMarket) {
      return moneylineMarkets.map((m, index) => {
        let color = '#888888';
        let name = (m as any).groupItemTitle || m.question || `Team ${index + 1}`;

        const nameLower = name.toLowerCase();
        const questionLower = m.question ? m.question.toLowerCase() : '';
        if (
          nameLower.includes('draw') ||
          nameLower.includes('tie') ||
          questionLower.includes('draw') ||
          questionLower.includes('tie')
        ) {
          color = '#888888';
          name = 'Draw';
        } else if (
          game.homeTeam?.name &&
          (nameLower.includes(game.homeTeam.name.toLowerCase()) ||
            questionLower.includes(game.homeTeam.name.toLowerCase()))
        ) {
          color = game.homeTeam.color || '#eb144c';
          name = game.homeTeam.name;
        } else if (
          game.awayTeam?.name &&
          (nameLower.includes(game.awayTeam.name.toLowerCase()) ||
            questionLower.includes(game.awayTeam.name.toLowerCase()))
        ) {
          color = game.awayTeam.color || '#0094ff';
          name = game.awayTeam.name;
        }

        return {
          id: `team${index + 1}`,
          name,
          color,
          marketId: `team${index + 1}`,
        };
      });
    } else {
      const outcomes = parseMarketOutcomes(moneylineMarkets[0].outcomes || []);
      const markets: any[] = [];

      if (tokenIds.length >= 1) {
        markets.push({
          id: 'team1',
          name: game.homeTeam?.name || outcomes[0] || 'Home',
          color: game.homeTeam?.color || '#eb144c',
          marketId: 'team1',
        });
      }

      if (tokenIds.length >= 2) {
        markets.push({
          id: 'team2',
          name: game.awayTeam?.name || outcomes[1] || 'Away',
          color: game.awayTeam?.color || '#0094ff',
          marketId: 'team2',
        });
      }

      if (tokenIds.length >= 3) {
        markets.push({
          id: 'team3',
          name: outcomes[2] || 'Draw',
          color: '#888888',
          marketId: 'team3',
        });
      }

      return markets;
    }
  }, [game, moneylineMarkets, isMultiMarket, tokenIds]);

  return (
    <Box sx={{ mb: 4 }}>
      <PriceChart
        markets={chartMarkets}
        data={historicalData}
        selectedRange="ALL"
        isLoading={isLoading}
      />
    </Box>
  );
};
